import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  Home, Sparkles, BookOpen, ChefHat, Camera, Calculator, ShoppingCart,
  Warehouse, BarChart3, Users, Settings, Bell, Search, LogOut, Menu as MenuIcon,
  TrendingUp, Shield, CreditCard, X, Mail,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Emblem } from "@/components/Logo";
import { InstallAppButton } from "@/components/InstallApp";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";
import api from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/create", label: "Create", icon: Sparkles },
  { to: "/menus", label: "Menus", icon: BookOpen },
  { to: "/recipes", label: "Recipes", icon: ChefHat },
  { to: "/plate-studio", label: "Plate Studio", icon: Camera },
  { to: "/pca", label: "PCA", icon: Calculator, mod: "pca" },
  { to: "/purchasing", label: "Purchasing", icon: ShoppingCart, mod: "purchasing" },
  { to: "/my-kitchen", label: "My Kitchen", icon: Warehouse, mod: "kitchen" },
  { to: "/profit", label: "Profit Mode", icon: TrendingUp, mod: "pca" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, mod: "pca" },
  { to: "/team", label: "Team", icon: Users, mod: "team" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotes(data.activity || [])).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-ink flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-50 h-screen w-64 shrink-0 bg-surface border-r border-white/5 flex flex-col
        transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/5">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Emblem className="h-8 w-8" />
            <span className="font-serif text-lg gold-text tracking-wide">CHEF PLATED</span>
          </Link>
          <button className="lg:hidden text-white/60" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.filter((n) => !n.mod || !user?.permissions || user.permissions[n.mod]?.view).map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-sm text-sm font-medium transition-colors min-h-[48px]
                ${isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/60 hover:text-white hover:bg-white/5"}`
              }>
              <n.icon size={18} strokeWidth={1.6} /> {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-sm text-sm font-medium transition-colors min-h-[48px]
                ${isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Shield size={18} strokeWidth={1.6} /> Admin
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/5">
          <Link to="/settings" onClick={() => setOpen(false)} data-testid="sidebar-profile" className="flex items-center gap-3 px-3 py-2.5 rounded-sm hover:bg-white/5 mb-1">
            <Avatar user={user} size={38} />
            <div className="min-w-0">
              <p className="text-sm text-white/90 truncate">{user?.name || "Chef"}</p>
              <p className="text-xs text-white/40 capitalize truncate">{(user?.role || "chef").replace(/_/g, " ")}</p>
            </div>
          </Link>
          <div className="px-3 pb-2"><InstallAppButton className="w-full btn-outline-gold px-3 py-2.5 text-xs uppercase tracking-widest rounded-sm flex items-center justify-center gap-2" label="Download App" /></div>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-3 rounded-sm text-sm text-white/60 hover:text-gold min-h-[48px]">
            <CreditCard size={18} strokeWidth={1.6} /> Subscription
          </Link>
          <Link to="/settings" className="flex items-center gap-3 px-3 py-3 rounded-sm text-sm text-white/60 hover:text-white min-h-[48px]">
            <Settings size={18} strokeWidth={1.6} /> Settings
          </Link>
          <button onClick={doLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-sm text-white/60 hover:text-white min-h-[48px]">
            <LogOut size={18} strokeWidth={1.6} /> Logout
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 glass h-16 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white/70" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><MenuIcon size={22} /></button>
            <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-sm bg-white/5 border border-white/5 w-72">
              <Search size={16} className="text-white/40" />
              <input placeholder="Search recipes, menus…" data-testid="global-search"
                className="bg-transparent outline-none text-sm text-white/80 w-full placeholder:text-white/30" />
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <div className="relative">
              <button data-testid="notifications-btn" onClick={() => setShowNotes((v) => !v)}
                className="relative p-2.5 rounded-sm hover:bg-white/5 text-white/70">
                <Bell size={20} strokeWidth={1.6} />
                {notes.length > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-gold" />}
              </button>
              {showNotes && (
                <div className="absolute right-0 mt-2 w-80 bg-elevated border border-white/10 rounded-sm shadow-2xl p-2 z-50">
                  <p className="label-eyebrow px-3 py-2">Activity</p>
                  {notes.length === 0 && <p className="px-3 py-4 text-sm text-white/40">No recent activity.</p>}
                  {notes.slice(0, 8).map((a) => (
                    <div key={a.id} className="px-3 py-2.5 text-sm text-white/70 hover:bg-white/5 rounded-sm">
                      <span className="text-gold">{a.actor}</span> {a.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Link to="/settings" className="flex items-center gap-2.5 pl-2">
              <div className="h-9 w-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold font-semibold text-sm">
                {(user?.name || "C")[0].toUpperCase()}
              </div>
              <div className="hidden md:block leading-tight">
                <p className="text-sm text-white/90 font-medium">{user?.name}</p>
                <p className="text-[11px] text-gold capitalize">{(user?.plan || "chef").replace("_", " ")}</p>
              </div>
            </Link>
          </div>
        </header>
        {!user?.email_verified && user?.role !== "admin" && <VerifyBanner />}
        <main className="flex-1 p-4 md:p-8 max-w-[1500px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}


function VerifyBanner() {
  const [sent, setSent] = useState(false);
  const resend = async () => {
    try { const { data } = await api.post("/auth/resend-verification"); toast.success(data.message); setSent(true); }
    catch { toast.error("Could not send. Try again shortly."); }
  };
  return (
    <div data-testid="verify-banner" className="bg-gold/10 border-b border-gold/30 px-4 lg:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
      <p className="text-sm text-gold/90 flex items-center gap-2">
        <Mail size={16} /> Please verify your email to unlock menu generation. Check your inbox for the confirmation link.
      </p>
      <button onClick={resend} disabled={sent} data-testid="banner-resend-btn"
        className="text-xs uppercase tracking-widest text-gold hover:underline disabled:opacity-50 shrink-0">
        {sent ? "Email sent" : "Resend email"}
      </button>
    </div>
  );
}
